export type UserGraphDto = {
  key: string
  base_key: string
  s3_location: string
  s3_hash: string
  creator_email: string
  creation_date: string
  base_graph_family_key: string
  last_updated: string
  owner_group_key: string
  dashboard_s3_location?: string
  dashboard_s3_hash?: string
}

export type GraphDto = {
  key: string
  name: string
  product_key: string
  product_organisation_key: string
  product_division_key: string
  creator_email: string
  creation_date: string
  last_updated: string
  base_graph_family_key: string
  description: string
  s3_location: string
  s3_hash: string
  dashboard_s3_location?: string
  dashboard_s3_hash?: string
  version: string
  published: boolean
  owner_group_key: string
  start_date: string
  end_date: string
  rolling_duration: string | number
  yearly_rolling_start_month: number | null
  sampler_version: string
  simulation_frequency: "yearly" | "quarterly" | "monthly" | (string & {})
  parametrizable: boolean
  scenario_source: string
  scenario_version: string
  scenarios: string[]
}

export type GraphsDto = {
  items: GraphDto[]
  total: number
}

export type UpdateGraphDto = {
  last_updated: string
  description: string
  published: boolean
  start_date: string
  end_date: string
  startDate: string
  endDate: string
  rolling_duration: string
  yearly_rolling_start_month: number
  simulation_frequency: "yearly" | "quarterly" | "monthly" | (string & {})
  sampler_version: string
  scenario_version: string
  scenario_source: string
}

export enum TKRISKGraphNodeType {
  // these are the types supported by TKRISK
  // all GraphNodeType nodes must be converted/compatible with these types
  CONTINOUS = "continuous",
  MIXTURE = "mixture",
  DISCRETE = "discrete",
  CATEGORICAL = "categorical",
  DETERMINISTIC = "deterministic",
  SCENARIO_TABLE = "scenarioTable",
  FORECAST_TABLE = "forecastTable",
  TABLE = "table",
  GROUP = "group",
}

export enum GraphNodeType {
  CONTINOUS = TKRISKGraphNodeType.CONTINOUS,
  MIXTURE = TKRISKGraphNodeType.MIXTURE,
  DISCRETE = TKRISKGraphNodeType.DISCRETE,
  CATEGORICAL = TKRISKGraphNodeType.CATEGORICAL,
  DETERMINISTIC = TKRISKGraphNodeType.DETERMINISTIC,
  SCENARIO_TABLE = TKRISKGraphNodeType.SCENARIO_TABLE,
  FORECAST_TABLE = TKRISKGraphNodeType.FORECAST_TABLE,
  TABLE = TKRISKGraphNodeType.TABLE,
  GROUP = TKRISKGraphNodeType.GROUP,

  // additional pseudo-node types
  LAG = "lag",
  FORECAST = "forecast",
  SCENARIO = "scenario",
}

export enum DistributionParam {
  c = "c",
  loc = "loc",
  scale = "scale",
  lbound = "lbound",
  ubound = "ubound",
  kappa = "kappa",
  df = "df",
  a = "a",
  b = "b",
  dfn = "dfn",
  dfd = "dfd",
  nc = "nc",
  s = "s",
  p = "p",
  mu = "mu",
  n = "n",
  N = "N",
  M = "M",
}

export type DistributionParams =
  | "c"
  | "loc"
  | "scale"
  | "lbound"
  | "ubound"
  | "kappa"
  | "df"
  | "a"
  | "b"
  | "dfn"
  | "dfd"
  | "nc"
  | "s"
  | "p"
  | "mu"
  | "n"
  | "N"
  | "M"

export enum Distribution {
  Binomial = "Binomial",
  Geometric = "Geometric",
  Hypergeometric = "Hypergeometric",
  Logseries = "Logseries",
  Poisson = "Poisson",
  Weibull = "Weibull",
  Wald = "Wald",
  Vonmises = "Vonmises",
  Uniform = "Uniform",
  Triangular = "Triangular",
  T = "T",
  Rayleigh = "Rayleigh",
  Power = "Power",
  Pareto = "Pareto",
  Normal = "Normal",
  NonCentralF = "NonCentralF",
  NonCentralChisquare = "NonCentralChisquare",
  Lognormal = "Lognormal",
  Logistic = "Logistic",
  Laplace = "Laplace",
  Gumbel = "Gumbel",
  Gamma = "Gamma",
  F = "F",
  Exponential = "Exponential",
  Chisquare = "Chisquare",
  Cauchy = "Cauchy",
  Beta = "Beta",
  Categorical = "Categorical",
}

export type Distributions =
  | "Binomial"
  | "Geometric"
  | "Hypergeometric"
  | "Logseries"
  | "Poisson"
  | "Weibull"
  | "Wald"
  | "Vonmises"
  | "Uniform"
  | "Triangular"
  | "T"
  | "Rayleigh"
  | "Power"
  | "Pareto"
  | "Normal"
  | "NonCentralF"
  | "NonCentralChisquare"
  | "Lognormal"
  | "Logistic"
  | "Laplace"
  | "Gumbel"
  | "Gamma"
  | "F"
  | "Exponential"
  | "Chisquare"
  | "Cauchy"
  | "Beta"
  | "Categorical"

export const isDistributionParam = (param: string): param is DistributionParam => {
  return Object.values<string>(DistributionParam).includes(param)
}

type MnemonicRow = (string | number | null)[]

export type MnemonicData = {
  empty: boolean
  columns: string[]
  rows: MnemonicRow[]
}

export type ScenarioFileRow = {
  Value: string
  Date: string
  /** if empty string - it is forecast row, otherwise scenario row */
  Scenario: string
  Mnem: string
}

export enum GraphApprovalStatus {
  Waiting = "Waiting",
  Denied = "Denied",
  Approved = "Approved",
}

export type GraphApproval = {
  approval_status: GraphApprovalStatus
  key: string
  run_group_key: string
  graph_key: string
  family: string
  target_graph_version: string // e.g. "1.0.0"
  creation_date: string
  last_updated: string
  ttl_datetime: string
  creator_email: string
  comments: string
}
